import React, {useState} from 'react';
import {Card} from "../card/card.jsx";
import ModalAttaches from "../modal/modal-attaches.jsx";
import {FaEye, FaRegImages} from "react-icons/fa";
import InputForm from "../input/input-form.jsx";
import Button from "../button/button.jsx";
import {v4} from "uuid";
import projectUtils from "../../utils/project-utils.js";

const API_URL = import.meta.env.VITE_API_URL;
const API_KEEPER = import.meta.env.VITE_API_KEEPER;
export const imgExtensions = ['jpg', 'jpeg', 'png', 'image/jpg', 'image/jpeg', 'image/png', 'gif', 'svg'];

const Dropzone = ({
    title,
    attaches,
    setAttaches,
    attachesDescription = undefined,
    setAttachesDescription,
    withoutCard,
    disabled = false,
    attachUrl = API_KEEPER + "/ged/attach/",
    pdfUrl = API_KEEPER + "/ged/attach/print/pdf/",
    accept = "image/*,application/pdf",
    tagLabel = undefined,
    acceptOthers =true,
    resolveAttachUrl,
    canRemove = () => true,
    getAttachDescription,
    onAttachDescriptionChange,
    acceptedMimeTypes,
    invalidFileTypeMessage = "Tipo de arquivo não permitido. Selecione somente arquivos compatíveis.",
}) => {
    const [modal, setModal] = useState({show: false, file: undefined});

    const onClick = (e) => {
        if (disabled) return;

        const excludeClass = ["dropzone-preview", "dropzone-image", "dropzone-button-group", "dropzone-button", "dropzone-input"];
        if (excludeClass.some(className => e.target.classList?.contains(className))) return;

        const input = document.createElement('input');
        input.type = 'file';
        input.accept = acceptedMimeTypes?.length ? acceptedMimeTypes.join(',') : accept;
        input.multiple  = true;
        input.onchange = event => processFile([...event.target.files]);

        input.click();
    }

    const onDrop = async (e) => {
        e.preventDefault();

        if (disabled) return;

        let files;
        if (e.dataTransfer.items) {
            const items = [...e.dataTransfer.items];
            files = items.filter(item => item.kind === 'file').map(item => item?.getAsFile());
        } else {
            files = [...e.dataTransfer.files];
        }

        processFile(files)
    }

    const isAccepted = (file) => {
        const name = file.name?.toLowerCase() ?? "";
        const type = file.type?.toLowerCase() ?? "";
        const acceptedTypes = accept.split(",").map(accepted => accepted.trim().toLowerCase()).filter(Boolean);

        return acceptedTypes.some(accepted => {
            if (accepted.startsWith(".")) return name.endsWith(accepted);
            if (accepted.endsWith("/*")) return type.startsWith(accepted.slice(0, -1));
            return type === accepted;
        });
    }

    const closeModal = () => setModal(prev => ({...prev, show: false}));

    const openModal = (file) => setModal({
        show: true,
        file: resolveAttachUrl && file?.attachId && !file?.file
            ? {...file, url: resolveAttachUrl(file)}
            : file,
    });

    const processFile = (files) => {
        const matchesAcceptedMimeType = (file) => {
            if (acceptedMimeTypes?.length) {
                return acceptedMimeTypes.some((acceptedType) =>
                    acceptedType.endsWith('/*')
                        ? file.type?.startsWith(acceptedType.slice(0, -1))
                        : file.type === acceptedType
                );
            }

            return acceptOthers || isAccepted(file);
        };

        const invalidFiles = files.filter((file) => !matchesAcceptedMimeType(file));

        if (invalidFiles.length) {
            projectUtils.swalErrorMessage("", invalidFileTypeMessage);
        }

        const acceptedFiles = files.filter(matchesAcceptedMimeType);

        for (const file of acceptedFiles) {
            if (file.size > 20 * 1024 * 1024) {
                projectUtils.swalErrorMessage(
                    "",
                    "Tamanho máximo de arquivo excedido. Limite: 20MB"
                );
                return;
            }
        }

        const filesToAdd = acceptedFiles
            .filter(
                (file) =>
                    !attaches.some((p) => p.name === file.name) ||
                    imgExtensions.includes(file.type) ||
                    file.type === "application/pdf"
            )
            .map((file) => {
                file.attachId = v4();
                file.url = URL.createObjectURL(file);
                file.new = true;

                return file;
            });

        if (setAttachesDescription) {
            setAttachesDescription((prev) => ({
                ...prev,
                ...filesToAdd.reduce((acc, file) => {
                    acc[file.attachId] = file.description ?? null;
                    return acc;
                }, {}),
            }));
        }

        setAttaches((prev) => [...prev, ...filesToAdd]);
    };

    const removeFile = (file) => {
        const id = file.attachId
        setAttaches(prev => prev.filter(p => p?.attachId !== id));

        if (setAttachesDescription) {
            setAttachesDescription(prev => {
                delete prev[id]

                return prev
            });
        }

        closeModal()
    }

    const handleAttachDescription = (target, attachId) => {
        if (onAttachDescriptionChange) {
            onAttachDescriptionChange(attachId, target.value);
            return;
        }

        setAttachesDescription(prev => ({
            ...prev,
            [attachId]: target.value
        }));
    };

    const content = (
        <Card.Body>
            <em className="text-red-700 text-xs">Tamanho máximo permitido 20MB</em>
            <Card>
                <div id="dropzone" className={"w-full h-full flex justify-center flex-col cursor-pointer p-3"}
                     onClick={onClick} onDragOver={(e) => e.preventDefault()}
                     onDrop={onDrop}
                >
                    {
                        !attaches.length ?
                            <div id="text-div" className="flex flex-col items-center">
                                <FaRegImages className={"dropzone-text-img"} size={"5rem"}/>
                                <h4 id="dropzone-text" className="text-center font-bold">
                                    Clique aqui ou<br/> arraste e solte um arquivo
                                </h4>
                            </div>
                            :
                            <div className="flex flex-wrap gap-2">
                                {
                                    attaches.map((file, i) => {
                                        const ext = file.type ?? file.extension

                                        let url = file.new || file.file
                                            ? file.url
                                            : (resolveAttachUrl?.(file) ?? attachUrl + file.attachId);
                                        if (!imgExtensions.includes(ext)) {
                                            url = API_URL + "/public/assets/img/file-types/" + file.name?.split('.').pop() + '.png';
                                        }

                                        return (
                                            <div className={["dropzone-preview relative w-[180px] border border-[#bdbdbd] rounded-sm p-1 space-y-3", (attachesDescription ? "h-[250px]" : "h-[215px]")].join(" ")} key={i}>
                                                {
                                                    tagLabel &&
                                                    <span className="pointer-events-none absolute top-1.5 left-1.5 rounded-sm bg-black/60 px-1.5 py-0.5 text-[11px] text-white">
                                                        {tagLabel(file)}
                                                    </span>
                                                }

                                                <div
                                                    className="dropzone-image h-[150px] w-auto bg-contain bg-no-repeat"
                                                    style={{backgroundImage: "url(" + url + ")", backgroundPosition: "center 50%"}}
                                                    onClick={() => openModal(file)}>
                                                </div>

                                                {
                                                    file.datePhotoFace &&
                                                    <small className={"absolute top-[153px]"}>Data da foto: {projectUtils.formatDate(file.datePhotoFace, 'DD/MM/YYYY')}</small>
                                                }

                                                {
                                                    (attachesDescription || getAttachDescription) &&
                                                    <InputForm
                                                        id={"description-" + file.attachId}
                                                        value={getAttachDescription
                                                            ? (getAttachDescription(file) ?? "")
                                                            : (attachesDescription[file.attachId] ?? "")}
                                                        classNameWrapper={"dropzone-input col-span-2"}
                                                        inputClass={"dropzone-input"}
                                                        classNameLabel={"dropzone-input"}
                                                        placeholder={"Descrição"}
                                                        disabled={disabled}
                                                        onChange={({target}) => handleAttachDescription(target, file.attachId)}
                                                    />
                                                }

                                                <div className="dropzone-button-group w-full place-items-center">
                                                    <Button className="dropzone-button" onClick={() => openModal(file)}>
                                                        <FaEye className={"dropzone-button"}/>
                                                    </Button>
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                    }
                </div>
            </Card>
        </Card.Body>
    )

    const buildAttachName = () => {
        const {file} = modal

        if (file?.datePhotoFace) {
            return <small>Data da foto: {projectUtils.formatDate(file.datePhotoFace, 'DD/MM/YYYY')}</small>
        }

        return file?.name
    }

    return (
        <>
            {withoutCard ? (
                content
            ) : (
                <Card>
                    {title && (
                        <Card.Header>
                            <Card.Title>{title}</Card.Title>
                        </Card.Header>
                    )}
                    <Card.Body>
                        {content}
                    </Card.Body>
                </Card>
            )}

            <ModalAttaches
                title={buildAttachName()}
                open={modal.show}
                setOpen={closeModal}
                file={modal.file}
                removeFile={!disabled && canRemove(modal.file) ? removeFile : undefined}
                attachUrl={attachUrl}
                pdfUrl={pdfUrl}
            />
        </>
    );
};

export default Dropzone;
